import React from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Password } from 'primereact/password';

import { MACHINE_ACCESS_ROLES } from '../machineAccess';

const digitsOnly = (value) => (value || '').replace(/\D/g, '').slice(0, 6);

const MachineAccessProfileEditor = ({
	profile,
	hasStoredPin = false,
	errors,
	recipeOptions,
	onChange,
	onDelete,
	summaryFieldsInHeader = false,
}) => {
	const isTechnician = profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN;
	const isUser = profile.role === MACHINE_ACCESS_ROLES.USER;
	const roleLabel = isTechnician ? 'Technician' : isUser ? 'User' : 'Operator';
	const error = (field) => errors[`${profile.clientId}.${field}`];
	const inputClassName = (field) => `w-full ${error(field) ? 'p-invalid' : ''}`;

	const update = (field, value) => {
		const updatedProfile = {
			...profile,
			[field]: value,
		};
		if (isUser && field === 'recipeOrderTimeoutMinutes') {
			updatedProfile.machineAccessValidForMinutes = value;
		}
		onChange(updatedProfile);
	};

	return (
		<div className={`machine-access-profile machine-access-profile-${profile.role.toLowerCase()}`}>
			<div className="flex align-items-start justify-content-between gap-3 mb-3">
				<div>
					{!summaryFieldsInHeader && (
						<div className="flex align-items-center gap-2">
							<span className="machine-access-role-badge">{roleLabel}</span>
							{profile.profileId && <small className="text-500">Existing profile</small>}
						</div>
					)}
					{summaryFieldsInHeader && profile.profileId && (
						<small className="text-500">Existing profile</small>
					)}
					<p className="text-sm text-600 mt-2 mb-0">
						{isTechnician
							? 'Permanent emergency access to the hidden configuration area. This profile remains available when optional machine access is disabled.'
							: isUser
							? 'Unlocks ordering directly with the limits configured below.'
							: 'Unlocks the operator area and may also enable ordering.'}
					</p>
				</div>
				{!isTechnician && !summaryFieldsInHeader && (
					<Button
						type="button"
						label={`Remove ${roleLabel.toLowerCase()}`}
						icon="pi pi-trash"
						severity="danger"
						text
						onClick={onDelete}
					/>
				)}
			</div>

			<div className="grid">
				{!summaryFieldsInHeader && (
				<div className="col-12 lg:col-6">
					<div className="flex flex-column gap-2">
						<label htmlFor={`${profile.clientId}-name`} className="text-sm font-medium text-700">
							{roleLabel} name
						</label>
						<InputText
							id={`${profile.clientId}-name`}
							value={profile.name}
							onChange={(event) => update('name', event.target.value)}
							maxLength={100}
							placeholder={`Enter ${roleLabel.toLowerCase()} name`}
							className={inputClassName('name')}
						/>
						<small className="text-xs text-500">
							Shown in access sessions, operator tools, and order history so activity can be attributed.
						</small>
						{error('name') && <small className="p-error">{error('name')}</small>}
					</div>
				</div>
				)}

				{isTechnician ? (
					<>
						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<label htmlFor={`${profile.clientId}-new-pin`} className="text-sm font-medium text-700">
									New technician PIN
								</label>
								<Password
									inputId={`${profile.clientId}-new-pin`}
									value={profile.newPin}
									onChange={(event) => update('newPin', digitsOnly(event.target.value))}
									feedback={false}
									toggleMask
									maxLength={6}
									inputMode="numeric"
									placeholder={hasStoredPin ? 'Leave blank to keep the stored PIN' : 'Enter 6 digits'}
									className="w-full"
									inputClassName={inputClassName('pin')}
								/>
								<small className="text-xs text-500">
									The stored technician PIN is never displayed. Entering a replacement is optional; the eye
									button may be used only while checking the new entry.
								</small>
								{error('pin') && <small className="p-error">{error('pin')}</small>}
							</div>
						</div>

						<div className="col-12 lg:col-6 lg:col-offset-6">
							<div className="flex flex-column gap-2">
								<label htmlFor={`${profile.clientId}-confirm-pin`} className="text-sm font-medium text-700">
									Repeat new technician PIN
								</label>
								<Password
									inputId={`${profile.clientId}-confirm-pin`}
									value={profile.confirmPin}
									onChange={(event) => update('confirmPin', digitsOnly(event.target.value))}
									feedback={false}
									toggleMask
									maxLength={6}
									inputMode="numeric"
									placeholder="Repeat the same 6 digits"
									className="w-full"
									inputClassName={inputClassName('confirmPin')}
								/>
								<small className="text-xs text-500">
									Both entries must match before a replacement technician PIN can be saved.
								</small>
								{error('confirmPin') && <small className="p-error">{error('confirmPin')}</small>}
							</div>
						</div>
					</>
				) : (
					<div
						className={`col-12 ${summaryFieldsInHeader && !isUser ? 'lg:col-4' : 'lg:col-6'}`}
					>
						<div className="flex flex-column gap-2">
							<label htmlFor={`${profile.clientId}-pin`} className="text-sm font-medium text-700">
								{roleLabel} PIN
							</label>
							<Password
								inputId={`${profile.clientId}-pin`}
								value={profile.pin}
								onChange={(event) => update('pin', digitsOnly(event.target.value))}
								feedback={false}
								toggleMask
								maxLength={6}
								inputMode="numeric"
								placeholder="Enter 6 digits"
								className="w-full"
								inputClassName={inputClassName('pin')}
							/>
							<small className="text-xs text-500">
								Must be exactly 6 digits and unique across every technician, operator, and user on this machine.
							</small>
							{error('pin') && <small className="p-error">{error('pin')}</small>}
						</div>
					</div>
				)}

				{!isUser && (
					<div className="col-12 lg:col-4">
						<div className="flex flex-column gap-2">
							<label htmlFor={`${profile.clientId}-access-duration`} className="text-sm font-medium text-700">
								Machine access duration
							</label>
							<InputNumber
								inputId={`${profile.clientId}-access-duration`}
								value={profile.machineAccessValidForMinutes}
								onValueChange={(event) => update('machineAccessValidForMinutes', event.value)}
								min={1}
								maxFractionDigits={0}
								suffix=" min"
								className={inputClassName('machineAccessValidForMinutes')}
							/>
							<small className="text-xs text-500">
								How long the operator or hidden configuration area remains unlocked after this PIN is accepted.
							</small>
							{error('machineAccessValidForMinutes') && (
								<small className="p-error">{error('machineAccessValidForMinutes')}</small>
							)}
						</div>
					</div>
				)}

				<div className={`col-12 ${isUser ? 'lg:col-6' : 'lg:col-4'}`}>
					<div className="flex flex-column gap-2">
						<label htmlFor={`${profile.clientId}-ordering-duration`} className="text-sm font-medium text-700">
							Ordering access duration
						</label>
						<InputNumber
							inputId={`${profile.clientId}-ordering-duration`}
							value={profile.recipeOrderTimeoutMinutes}
							onValueChange={(event) => update('recipeOrderTimeoutMinutes', event.value)}
							min={1}
							maxFractionDigits={0}
							suffix=" min"
							className={inputClassName('recipeOrderTimeoutMinutes')}
						/>
						<small className="text-xs text-500">
							How long recipe ordering remains available after this PIN is accepted.
						</small>
						{error('recipeOrderTimeoutMinutes') && (
							<small className="p-error">{error('recipeOrderTimeoutMinutes')}</small>
						)}
					</div>
				</div>

				{!summaryFieldsInHeader && (
				<div className="col-12 lg:col-4">
					<div className="flex flex-column gap-2">
						<label htmlFor={`${profile.clientId}-maximum-recipes`} className="text-sm font-medium text-700">
							Maximum recipes
						</label>
						<InputNumber
							inputId={`${profile.clientId}-maximum-recipes`}
							value={profile.maximumRecipes}
							onValueChange={(event) => update('maximumRecipes', event.value)}
							min={0}
							maxFractionDigits={0}
							className={inputClassName('maximumRecipes')}
						/>
						<small className="text-xs text-500">
							Maximum orders per allowance period. Use 0 for unlimited.
						</small>
						{error('maximumRecipes') && <small className="p-error">{error('maximumRecipes')}</small>}
					</div>
				</div>
				)}

				<div className="col-12">
					<div className="flex flex-column gap-2">
						<label htmlFor={`${profile.clientId}-recipes`} className="text-sm font-medium text-700">
							Allowed recipes
						</label>
						<MultiSelect
							inputId={`${profile.clientId}-recipes`}
							value={profile.recipeIds}
							options={recipeOptions}
							onChange={(event) => update('recipeIds', event.value)}
							optionLabel="label"
							optionValue="value"
							placeholder="All recipes"
							display="chip"
							filter
							className="w-full"
						/>
						<small className="text-xs text-500">
							Leave empty to allow every recipe; select recipes to restrict this profile to only those choices.
						</small>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MachineAccessProfileEditor;
